// The option-set half of the form JSON import (z8nrz7f3qt, z8nrz7e698). Pure,
// like `src/lib/formTransfer.ts`, so the rules are unit-tested; the dialog only
// fetches and renders.
//
// A dropdown bound to an option set carries the SOURCE tenant's set id, which
// means nothing in any other tenant. The file therefore carries each set by
// value (`FormExportOptionSet`), and the import binds the field to a set in the
// target. Which set is decided by the server
// (`POST /form-definitions/import/option-sets`), from the same resolver the
// commit runs, so what the preview says is what the import does:
//
//   reused    the target has a set with this name and these values, in order
//   missing   no set of this name here
//   conflict  a set of this name holds different values
//
// For a missing or conflicting set the user picks: create it (a conflict is
// created as "<name> (imported)", and the existing set is never touched), or
// put its values on the fields as inline options. Inline is the default, and
// the only choice without the Option Sets permission.

import type { FieldDefinition, SectionDefinition } from '@/types/cgt'
import type { FormExportBody, FormExportOptionSet } from '@/lib/formTransfer'

export type OptionSetImportStatus = 'reused' | 'missing' | 'conflict'

/** What the server says it would do with one set. */
export interface OptionSetResolution {
  ref: string
  status: OptionSetImportStatus
  /** The name the set has (reused) or will be given (created). */
  name: string
  /** For `reused`: the target's set. */
  id?: string
}

export type OptionSetChoice = 'create' | 'inline'

/** One set as it travels to `POST /form-definitions/import`. */
export interface ImportOptionSetPayload {
  ref: string
  name: string
  options: Array<{ id: string; label: string }>
}

/** A set the file carries, with the fields that use it. */
export interface CarriedOptionSetRef {
  ref: string
  name: string
  values: string[]
  fieldLabels: string[]
}

/** A set a field names but the file does not carry (a version-1 file). */
export interface UncarriedOptionSetRef {
  ref: string
  fieldLabels: string[]
}

/**
 * The option sets the form's dropdowns name, split by whether the file carries
 * their values. One entry per set, however many fields share it (AC4.2).
 */
export function optionSetReferences(
  form: Pick<FormExportBody, 'sections'>,
  fileSets: readonly FormExportOptionSet[],
): { carried: CarriedOptionSetRef[]; uncarried: UncarriedOptionSetRef[] } {
  const fileSetById = new Map(fileSets.map(s => [s.id, s]))
  const labelsByRef = new Map<string, string[]>()
  for (const section of form.sections) {
    for (const field of section.fields) {
      if (!field.optionSetId) continue
      const labels = labelsByRef.get(field.optionSetId) ?? []
      labels.push(field.label)
      labelsByRef.set(field.optionSetId, labels)
    }
  }

  const carried: CarriedOptionSetRef[] = []
  const uncarried: UncarriedOptionSetRef[] = []
  for (const [ref, fieldLabels] of labelsByRef) {
    const set = fileSetById.get(ref)
    if (set) carried.push({ ref, name: set.name, values: set.values, fieldLabels })
    else uncarried.push({ ref, fieldLabels })
  }
  return { carried, uncarried }
}

/** The AC4.1 refusal: a version-1 file naming a set this tenant does not have. */
export const OPTION_SET_VALUES_MISSING_MESSAGE =
  "Option set values aren't included in this file. Re-export the form from the source company and try again."

/** The AC2.3 notice, shown in place of the Create choice. */
export const OPTION_SET_PERMISSION_MESSAGE =
  'Creating option sets requires the Option Sets permission. These values will import as inline options.'

/** One row of the preview's Option sets group. */
export interface OptionSetImportRow {
  ref: string
  /** The set's name in the file. */
  sourceName: string
  values: string[]
  fieldLabels: string[]
  resolution: OptionSetResolution
}

export interface OptionSetImportPlan {
  rows: OptionSetImportRow[]
  /**
   * Sets a version-1 file names that exist here, by id. Left bound exactly as
   * the file has them — the import proceeds as it did before this change.
   */
  keptRefs: string[]
  /** Non-empty when the import cannot proceed. */
  blockers: string[]
}

/**
 * Joins the file's references with the server's resolutions and with which of
 * the uncarried ids this tenant actually has.
 */
export function planOptionSetImport(
  refs: { carried: CarriedOptionSetRef[]; uncarried: UncarriedOptionSetRef[] },
  resolutions: readonly OptionSetResolution[],
  existingIds: ReadonlySet<string>,
): OptionSetImportPlan {
  const resolutionByRef = new Map(resolutions.map(r => [r.ref, r]))
  const rows: OptionSetImportRow[] = refs.carried.map(set => ({
    ref: set.ref,
    sourceName: set.name,
    values: set.values,
    fieldLabels: set.fieldLabels,
    // The server answers for every set it was sent; a missing answer is read
    // as "missing", the cautious reading — it offers the inline default.
    resolution: resolutionByRef.get(set.ref) ?? {
      ref: set.ref,
      status: 'missing',
      name: set.name,
    },
  }))
  const keptRefs = refs.uncarried.filter(r => existingIds.has(r.ref)).map(r => r.ref)
  const blockers = refs.uncarried.some(r => !existingIds.has(r.ref))
    ? [OPTION_SET_VALUES_MISSING_MESSAGE]
    : []
  return { rows, keptRefs, blockers }
}

/** True when a row needs the user to choose. */
export function needsChoice(row: OptionSetImportRow): boolean {
  return row.resolution.status !== 'reused'
}

/**
 * The choice actually applied: without the permission every set imports
 * inline, whatever was picked; otherwise the pick, defaulting to inline.
 */
export function effectiveChoice(
  row: OptionSetImportRow,
  choices: Readonly<Record<string, OptionSetChoice>>,
  canCreate: boolean,
): OptionSetChoice | null {
  if (!needsChoice(row)) return null
  if (!canCreate) return 'inline'
  return choices[row.ref] ?? 'inline'
}

/** The AC2.4 one-liner: what this row will produce. */
export function optionSetOutcome(row: OptionSetImportRow, choice: OptionSetChoice | null): string {
  if (choice === null) return `Uses the existing option set "${row.resolution.name}".`
  if (choice === 'create') return `Creates the option set "${row.resolution.name}".`
  const n = row.values.length
  return `${n} value${n === 1 ? '' : 's'} will import as inline options.`
}

/**
 * The form and option sets to send, with every choice applied.
 *
 * - **inline**: the fields get the set's values as `options` and lose their
 *   `optionSetId`, the same thing switching a field from Option Set to Inline
 *   does in the builder.
 * - **create** and **reused**: the field keeps the ref and the set travels with
 *   the request; the server binds it to the set it reuses or creates.
 *
 * Fields bound to a `keptRefs` set are left exactly as they are.
 */
export function applyOptionSetChoices(
  form: FormExportBody,
  rows: readonly OptionSetImportRow[],
  choices: Readonly<Record<string, OptionSetChoice>>,
  canCreate: boolean,
  newId: () => string = () => crypto.randomUUID(),
): { form: FormExportBody; optionSets: ImportOptionSetPayload[] } {
  const inlineValues = new Map<string, string[]>()
  const optionSets: ImportOptionSetPayload[] = []
  for (const row of rows) {
    if (effectiveChoice(row, choices, canCreate) === 'inline') {
      inlineValues.set(row.ref, row.values)
      continue
    }
    optionSets.push({
      ref: row.ref,
      name: row.sourceName,
      options: row.values.map(label => ({ id: newId(), label })),
    })
  }

  const toInline = (field: FieldDefinition): FieldDefinition => {
    const values = field.optionSetId ? inlineValues.get(field.optionSetId) : undefined
    if (!values) return field
    const { optionSetId: _dropped, ...rest } = field
    return { ...rest, options: [...values] }
  }
  const sections: SectionDefinition[] = form.sections.map(section => ({
    ...section,
    fields: section.fields.map(toInline),
  }))
  return { form: { ...form, sections }, optionSets }
}
